'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Error helper ─────────────────────────────────────────────────────────────
function isSchemaError(error: { code?: string; message?: string }): boolean {
  if (!error) return false;
  if (error.code) {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

// ─── Types (mirroring DB schema) ──────────────────────────────────────────────
export type RiskTier = 'Critical' | 'High' | 'Moderate' | 'Low';
export type LifecycleStage = 'Intake' | 'Sourcing' | 'Due Diligence' | 'Contracting' | 'Onboarding' | 'Active' | 'Renewal' | 'Terminated';
export type ContractStatus = 'Draft' | 'Legal Review' | 'Negotiation' | 'Pending Signature' | 'Active' | 'Expiring' | 'Terminated' | 'Archived';
export type DDStatus = 'Not Started' | 'In Progress' | 'Pending Approval' | 'Complete' | 'Overdue' | 'Waived';
export type InsuranceStatus = 'Current' | 'Expiring Soon' | 'Expired' | 'Not Required' | 'Missing';
export type IssueSeverity = 'Critical' | 'High' | 'Moderate' | 'Low';
export type IssueStatus = 'New' | 'Under Review' | 'Remediation' | 'Pending Validation' | 'Closed' | 'Accepted Risk' | 'Escalated';
export type CAPStatus = 'Not Started' | 'In Progress' | 'Submitted' | 'Approved' | 'N/A';
export type ApprovalAction = 'Approved' | 'Rejected' | 'Returned' | 'Escalated';
export type RFPStatus = 'Draft' | 'Published' | 'Evaluation' | 'Awarded' | 'Cancelled' | 'Closed';
export type BidderStatus = 'Received' | 'Under Review' | 'Shortlisted' | 'Disqualified' | 'Awarded';
export type OverallVendorStatus = 'Good Standing' | 'Needs Attention' | 'At Risk' | 'Non-Compliant';

export interface DbVendor {
  id: string;
  vendor_id: string;
  name: string;
  dba?: string;
  category: string;
  services: string;
  department: string;
  department_owner: string;
  contract_owner: string;
  risk_tier: RiskTier;
  criticality: RiskTier;
  lifecycle_stage: LifecycleStage;
  contract_status: ContractStatus;
  dd_status: DDStatus;
  insurance_status: InsuranceStatus;
  contract_start?: string;
  contract_end?: string;
  next_review?: string;
  last_review?: string;
  residual_risk: RiskTier;
  data_handled: string[];
  systems_accessed: string[];
  hosting_model: string;
  open_issues: number;
  open_critical_issues: number;
  overall_status: OverallVendorStatus;
  created_at: string;
  updated_at: string;
}

export interface DbIssue {
  id: string;
  issue_ref: string;
  vendor_id?: string;
  vendor_name: string;
  title: string;
  category: string;
  severity: IssueSeverity;
  issue_status: IssueStatus;
  date_identified?: string;
  due_date?: string;
  description: string;
  assigned_owner: string;
  department: string;
  cap_status: CAPStatus;
  escalated: boolean;
  days_open: number;
  created_at: string;
  updated_at: string;
}

export interface DbDDReview {
  id: string;
  review_ref: string;
  vendor_id?: string;
  vendor_name: string;
  domain: string;
  reviewer: string;
  dd_status: DDStatus;
  due_date?: string;
  risk_tier: RiskTier;
  findings_count: number;
  completion_pct: number;
  created_at: string;
  updated_at: string;
}

export interface DbWorkflowApproval {
  id: string;
  vendor_id?: string;
  stage: string;
  approver: string;
  action: ApprovalAction;
  comment: string;
  created_at: string;
}

export interface DbMonitoringAssessment {
  id: string;
  vendor_id?: string;
  vendor_name: string;
  risk_tier: RiskTier;
  department: string;
  last_assessment?: string;
  due_date?: string;
  days_overdue: number;
  assessment_type: string;
  assigned_to: string;
  assess_status: string;
  sla_performance: number;
  insurance_status: InsuranceStatus;
  compliance_score: number;
  security_refresh: string;
  incident_count: number;
  overall_score: number;
  trend: string;
  created_at: string;
  updated_at: string;
}

export interface DbRFP {
  id: string;
  rfp_ref: string;
  title: string;
  department: string;
  rfp_status: RFPStatus;
  publish_date?: string;
  close_date?: string;
  estimated_value: number;
  bidder_count: number;
  category: string;
  owner: string;
  created_at: string;
  updated_at: string;
}

export interface DbRFPBidder {
  id: string;
  rfp_id: string;
  company: string;
  contact_name: string;
  submitted_date?: string;
  quote_amount: number;
  bidder_status: BidderStatus;
  score_cost: number;
  score_technical: number;
  score_experience: number;
  score_security: number;
  score_responsible: number;
  notes: string;
  certifications: string[];
  years_experience: number;
  local_vendor: boolean;
  minority_owned: boolean;
  created_at: string;
}

export interface DbAuditEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  actor: string;
  actor_role: string;
  detail: string;
  created_at: string;
}

// ─── Vendor Service ───────────────────────────────────────────────────────────
export const vendorService = {
  async getAll(): Promise<DbVendor[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .order('name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Vendor fetch error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('vendorService.getAll error:', (err as Error).message);
      throw err;
    }
  },

  async getById(id: string): Promise<DbVendor | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (err: unknown) {
      console.log('vendorService.getById error:', (err as Error).message);
      throw err;
    }
  },

  async updateLifecycleStage(id: string, stage: LifecycleStage): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('vendors')
      .update({ lifecycle_stage: stage })
      .eq('id', id);
    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('updateLifecycleStage error:', error.message);
    }
  },

  subscribeToAll(callback: (vendors: DbVendor[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('vendors_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vendors' }, async () => {
        const vendors = await vendorService.getAll();
        callback(vendors);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};

// ─── Issue Service ────────────────────────────────────────────────────────────
export const issueService = {
  async getAll(): Promise<DbIssue[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('issues')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Issue fetch error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('issueService.getAll error:', (err as Error).message);
      throw err;
    }
  },

  async getByVendorId(vendorId: string): Promise<DbIssue[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('issues')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('issueService.getByVendorId error:', (err as Error).message);
      throw err;
    }
  },

  async updateStatus(id: string, status: IssueStatus): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('issues')
      .update({ issue_status: status })
      .eq('id', id);
    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('updateStatus error:', error.message);
    }
  },

  subscribeToAll(callback: (issues: DbIssue[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('issues_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'issues' }, async () => {
        const issues = await issueService.getAll();
        callback(issues);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};

// ─── DD Review Service ────────────────────────────────────────────────────────
export const ddReviewService = {
  async getAll(): Promise<DbDDReview[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('dd_reviews')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('ddReviewService.getAll error:', (err as Error).message);
      throw err;
    }
  },

  async getByVendorId(vendorId: string): Promise<DbDDReview[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('dd_reviews')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('ddReviewService.getByVendorId error:', (err as Error).message);
      throw err;
    }
  },
};

// ─── Workflow Approval Service ────────────────────────────────────────────────
export const workflowService = {
  async getByVendorId(vendorId: string): Promise<DbWorkflowApproval[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('workflow_approvals')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('workflowService.getByVendorId error:', (err as Error).message);
      throw err;
    }
  },

  async addApproval(approval: Omit<DbWorkflowApproval, 'id' | 'created_at'>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('workflow_approvals').insert(approval);
    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('addApproval error:', error.message);
    }
  },
};

// ─── Monitoring Service ───────────────────────────────────────────────────────
export const monitoringService = {
  async getAll(): Promise<DbMonitoringAssessment[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('monitoring_assessments')
        .select('*')
        .order('due_date', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('monitoringService.getAll error:', (err as Error).message);
      throw err;
    }
  },

  subscribeToAll(callback: (items: DbMonitoringAssessment[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('monitoring_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'monitoring_assessments' }, async () => {
        const items = await monitoringService.getAll();
        callback(items);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};

// ─── RFP Service ──────────────────────────────────────────────────────────────
export const rfpService = {
  async getAll(): Promise<DbRFP[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('rfps')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('rfpService.getAll error:', (err as Error).message);
      throw err;
    }
  },

  async getBidders(rfpId: string): Promise<DbRFPBidder[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('rfp_bidders')
        .select('*')
        .eq('rfp_id', rfpId)
        .order('quote_amount', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('rfpService.getBidders error:', (err as Error).message);
      throw err;
    }
  },

  subscribeToAll(callback: (rfps: DbRFP[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('rfps_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rfps' }, async () => {
        const rfps = await rfpService.getAll();
        callback(rfps);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};

// ─── Audit Trail Service ──────────────────────────────────────────────────────
export const auditService = {
  async getRecent(limit = 50): Promise<DbAuditEntry[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('audit_trail')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('auditService.getRecent error:', (err as Error).message);
      throw err;
    }
  },

  async getByEntity(entityType: string, entityId: string): Promise<DbAuditEntry[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('audit_trail')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: unknown) {
      console.log('auditService.getByEntity error:', (err as Error).message);
      throw err;
    }
  },

  async log(entry: Omit<DbAuditEntry, 'id' | 'created_at'>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('audit_trail').insert(entry);
    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('auditService.log error:', error.message);
    }
  },

  subscribeToRecent(callback: (entries: DbAuditEntry[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('audit_trail_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'audit_trail' }, async () => {
        const entries = await auditService.getRecent(50);
        callback(entries);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};
